import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { AdminDialogModal } from '@/components/common/admin/mobile/AdminDialogModal';
import { GlassFormInput } from '@/components/admin/redesign';
import { AdminTagChipPicker } from './AdminTagChipPicker';
import { SurveyEditPanel } from '@/components/residents/corkBoard/survey/SurveyEditPanel';
import { Button } from '@/components/residents/marketplace/shared/Button';
import { useGetThreadQuery } from '@/store/api/corkBoard/corkBoardApi';
import type { CorkBoardTag, CorkBoardThreadSummary, SurveySummary } from '@/types/residents/corkBoard';

interface AdminSurveyEditModalProps {
  visible: boolean;
  thread: CorkBoardThreadSummary | null;
  availableTags: CorkBoardTag[];
  isSaving: boolean;
  onSave: (data: { threadId: string; title: string; body: string; tagIds: number[] }) => void;
  onConfigChange: (patch: { threadId: string; expiresAt?: string | null; allowReplies?: boolean }) => void;
  onViewResponses: (thread: CorkBoardThreadSummary) => void;
  onClose: () => void;
}

export const AdminSurveyEditModal: React.FC<AdminSurveyEditModalProps> = memo(
  ({ visible, thread, availableTags, isSaving, onSave, onConfigChange, onViewResponses, onClose }) => {
    const threadId = thread?.id ?? '';

    const { data: detail } = useGetThreadQuery(
      { threadId, page: 0, pageSize: 1 },
      { skip: !visible || !threadId },
    );

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [titleError, setTitleError] = useState<string | undefined>(undefined);

    const detailThread = detail?.thread;
    const survey: SurveySummary | undefined = detailThread?.survey ?? thread?.survey;

    // Seed local state from the full thread detail. While the modal is open but
    // detail hasn't loaded yet, blank out so a previously-edited survey's values
    // don't flash before the new one resolves. The survey tag is excluded from
    // the editable selection (it's shown as a locked tag and managed like the
    // create flow, which never puts it in selectedTagIds).
    useEffect(() => {
      if (!visible) return;
      if (detailThread) {
        const surveyTagId = availableTags.find((t) => t.slug === 'survey')?.id;
        setTitle(detailThread.title);
        setBody(detailThread.body ?? '');
        setSelectedTagIds(
          detailThread.tags
            .map((t) => Number(t.id))
            .filter((id) => !Number.isNaN(id) && id !== surveyTagId),
        );
      } else {
        setTitle('');
        setBody('');
        setSelectedTagIds([]);
      }
      setTitleError(undefined);
    }, [visible, detailThread, availableTags]);

    const categoryTags = useMemo(
      () =>
        availableTags
          .filter((t) => t.slug !== 'announcements' && t.slug !== 'survey')
          .map((t) => (t.slug === 'events' ? { ...t, icon: null } : t)),
      [availableTags],
    );

    const surveyTag = useMemo(
      () => availableTags.find((t) => t.slug === 'survey') ?? null,
      [availableTags],
    );

    const isClosed = !!survey?.isClosed;

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [tagId]));
    }, []);

    const handleConfigChange = useCallback(
      (patch: { expiresAt?: string | null; allowReplies?: boolean }) => {
        if (!threadId) return;
        onConfigChange({ threadId, ...patch });
      },
      [threadId, onConfigChange],
    );

    const handleSave = useCallback(() => {
      if (isSaving || !threadId) return;
      if (!title.trim()) {
        setTitleError('Title is required');
        return;
      }
      onSave({ threadId, title: title.trim(), body: body.trim(), tagIds: selectedTagIds });
    }, [isSaving, threadId, title, body, selectedTagIds, onSave]);

    return (
      <AdminDialogModal
        visible={visible}
        title="Edit Survey"
        onClose={onClose}
        onSave={handleSave}
        isSaving={isSaving}
        saveText="Save Changes"
        savingText="Saving..."
        maxWidth={680}
      >
        <GlassFormInput
          label="Title"
          required
          value={title}
          onChangeText={(text) => {
            setTitle(text);
            if (titleError) setTitleError(undefined);
          }}
          placeholder="Survey title"
          maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
          error={titleError}
        />

        <GlassFormInput
          label="Body"
          value={body}
          onChangeText={setBody}
          placeholder="Optional context shown above the questions..."
          multiline
          maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
        />

        <View style={styles.field}>
          <AdminTagChipPicker
            tags={categoryTags}
            selectedTagIds={selectedTagIds}
            onToggleTag={handleToggleTag}
            lockedTag={surveyTag}
            label="Category tag (optional)"
          />
        </View>

        {survey && (
          <SurveyEditPanel
            locked={survey.responseCount > 0}
            survey={survey}
            onConfigChange={handleConfigChange}
            disabled={isClosed}
          />
        )}

        {thread && survey && (
          <Button
            title={`View all responses (${survey.responseCount})`}
            variant="outline"
            size="medium"
            fullWidth
            onPress={() => onViewResponses(thread)}
          />
        )}
      </AdminDialogModal>
    );
  },
);

AdminSurveyEditModal.displayName = 'AdminSurveyEditModal';

const styles = StyleSheet.create({
  field: { gap: 6 },
});
